import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "./App.css";

function ChatsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [websiteToken, setWebsiteToken] = useState(
    searchParams.get("token") || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const scriptLoadedRef = useRef(false);
  const timeoutRef = useRef(null);
  const widgetLoadedRef = useRef(false);

  // Usar a mesma lógica de BASE_URL do App.js
  const BASE_URL =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_CHATWOOT_URL || "http://localhost:3000"
      : "REACT_APP_CHATWOOT_URL_PLACEHOLDER";

  // Função para limpar o widget do Chatwoot
  const cleanupChatwoot = () => {
    // Remove o script do Chatwoot se existir
    const existingScript = document.querySelector(
      'script[src*="/packs/js/sdk.js"]'
    );
    if (existingScript) {
      existingScript.remove();
    }

    // Remove o container do widget
    const widgetContainer = document.getElementById(
      "chatwoot-widget-container"
    );
    if (widgetContainer) {
      widgetContainer.remove();
    }

    // Limpa variáveis globais
    if (window.$chatwoot) {
      try {
        window.$chatwoot.reset();
      } catch (e) {
        console.warn("Error resetting Chatwoot:", e);
      }
    }
    delete window.$chatwoot;
    delete window.chatwootSDK;
    delete window.chatwootSettings;

    // Remove event listeners
    const events = ["chatwoot:ready", "chatwoot:error", "popstate", "error"];
    events.forEach((event) => {
      const listeners = window[`_chatwoot_${event}_listeners`] || [];
      listeners.forEach((listener) => {
        window.removeEventListener(event, listener);
      });
      delete window[`_chatwoot_${event}_listeners`];
    });

    // Remover listener global de erros se existir
    try {
      window.removeEventListener(
        "error",
        window._chatwoot_global_error_handler
      );
      delete window._chatwoot_global_error_handler;
    } catch (e) {
      // Ignorar se não existir
    }

    setIsWidgetLoaded(false);
    scriptLoadedRef.current = false;
    widgetLoadedRef.current = false;

    // Limpar timeout se existir
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Função para carregar o widget do Chatwoot
  const loadChatwootWidget = (token = null) => {
    const tokenToUse = token || websiteToken;

    if (!tokenToUse) {
      setError("Por favor, insira um WEBSITE_TOKEN válido");
      return;
    }

    if (scriptLoadedRef.current) {
      cleanupChatwoot();
    }

    widgetLoadedRef.current = false;
    setIsLoading(true);
    setError(null);

    // Configurações do Chatwoot
    window.chatwootSettings = {
      hideMessageBubble: false,
      position: "right",
      locale: "pt",
      type: "expanded_bubble",
      launcherTitle: "Precisas de ajuda?",
      darkMode: "light",
      showPopoutButton: true,
      showUnreadMessagesDialog: true,
      useBrowserLanguage: false,
      enableFileUpload: true,
      enableEmojiPicker: true,
      welcomeTitle: "Precisas de ajuda?",
      welcomeDescription: "Eu posso ajudar-te a encontrar o que precisas.",
      availableMessage: "Estamos disponíveis para te ajudar.",
      unavailableMessage: "Estamos ausentes",
    };

    // Criar e carregar o script do Chatwoot
    const script = document.createElement("script");
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.defer = true;
    script.async = true;

    // Timeout para detectar token inválido
    // Limpar timeout anterior se existir
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (!widgetLoadedRef.current) {
        if (window._chatwoot_global_error_handler) {
          window.removeEventListener(
            "error",
            window._chatwoot_global_error_handler
          );
          delete window._chatwoot_global_error_handler;
        }
        setError(
          "Token inválido ou não encontrado. Por favor, verifique se o WEBSITE_TOKEN está correto."
        );
        setIsLoading(false);
        cleanupChatwoot();
      }
    }, 5000); // 5 segundos de timeout

    // Listener global para erros não capturados do SDK
    const globalErrorHandler = (event) => {
      // Verificar se o erro é relacionado ao Chatwoot SDK
      if (
        event.error &&
        (event.error.message?.includes("baseDomain") ||
          event.error.message?.includes("Chatwoot") ||
          event.error.message?.includes("Cannot read properties") ||
          event.filename?.includes("sdk.js") ||
          event.filename?.includes("chatwoot"))
      ) {
        console.error("Erro do Chatwoot SDK:", event.error);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setError(
          "Token inválido ou não encontrado. Por favor, verifique se o WEBSITE_TOKEN está correto."
        );
        setIsLoading(false);
        setIsWidgetLoaded(false);
        cleanupChatwoot();
        // Remover o listener após usar
        if (window._chatwoot_global_error_handler) {
          window.removeEventListener(
            "error",
            window._chatwoot_global_error_handler
          );
          delete window._chatwoot_global_error_handler;
        }
      }
    };

    // Armazenar o handler para poder removê-lo depois
    window._chatwoot_global_error_handler = globalErrorHandler;
    window.addEventListener("error", globalErrorHandler);

    script.onload = () => {
      if (window.chatwootSDK && typeof window.chatwootSDK.run === "function") {
        try {
          // Validar parâmetros antes de inicializar
          if (!tokenToUse || !tokenToUse.trim()) {
            throw new Error("Token inválido: token vazio");
          }

          // Verificar se a URL base é válida
          try {
            new URL(BASE_URL);
          } catch (e) {
            throw new Error("URL base inválida: formato incorreto");
          }

          // Inicializar o SDK com tratamento de erro adicional
          try {
            window.chatwootSDK.run({
              websiteToken: tokenToUse.trim(),
              baseUrl: BASE_URL.trim(),
            });
          } catch (sdkError) {
            // Se o SDK lançar um erro imediatamente, capturar aqui
            throw new Error(`Erro ao inicializar SDK: ${sdkError.message}`);
          }

          // Listener para erros do Chatwoot
          const errorHandler = (event) => {
            console.error("Erro do Chatwoot:", event);
            if (window._chatwoot_global_error_handler) {
              window.removeEventListener(
                "error",
                window._chatwoot_global_error_handler
              );
              delete window._chatwoot_global_error_handler;
            }
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setError(
              "Token inválido ou não encontrado. Por favor, verifique se o WEBSITE_TOKEN está correto."
            );
            setIsLoading(false);
            setIsWidgetLoaded(false);
            cleanupChatwoot();
          };

          window.addEventListener("chatwoot:error", errorHandler);
          window[`_chatwoot_chatwoot:error_listeners`] =
            window[`_chatwoot_chatwoot:error_listeners`] || [];
          window[`_chatwoot_chatwoot:error_listeners`].push(errorHandler);

          // Configurar quando o Chatwoot estiver pronto
          const readyHandler = () => {
            if (window._chatwoot_global_error_handler) {
              window.removeEventListener(
                "error",
                window._chatwoot_global_error_handler
              );
              delete window._chatwoot_global_error_handler;
            }
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            widgetLoadedRef.current = true;
            if (window.$chatwoot) {
              window.$chatwoot.setCustomAttributes({
                current_page_url: window.location.href,
              });
            }
            setIsWidgetLoaded(true);
            setIsLoading(false);
            // Override Chatwoot styles with larger dimensions
            setTimeout(overrideChatwootStyles, 100);
          };

          window.addEventListener("chatwoot:ready", readyHandler);
          window[`_chatwoot_chatwoot:ready_listeners`] =
            window[`_chatwoot_chatwoot:ready_listeners`] || [];
          window[`_chatwoot_chatwoot:ready_listeners`].push(readyHandler);

          // Atualizar URL quando a página muda
          const popstateHandler = () => {
            if (window.$chatwoot) {
              window.$chatwoot.setCustomAttributes({
                current_page_url: window.location.href,
              });
            }
          };

          window.addEventListener("popstate", popstateHandler);
          window[`_chatwoot_popstate_listeners`] =
            window[`_chatwoot_popstate_listeners`] || [];
          window[`_chatwoot_popstate_listeners`].push(popstateHandler);
        } catch (err) {
          if (window._chatwoot_global_error_handler) {
            window.removeEventListener(
              "error",
              window._chatwoot_global_error_handler
            );
            delete window._chatwoot_global_error_handler;
          }
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          console.error("Erro ao inicializar Chatwoot:", err);
          setError(
            "Erro ao carregar o widget do Chatwoot. Verifique se o WEBSITE_TOKEN está correto."
          );
          setIsLoading(false);
          cleanupChatwoot();
        }
      } else {
        if (window._chatwoot_global_error_handler) {
          window.removeEventListener(
            "error",
            window._chatwoot_global_error_handler
          );
          delete window._chatwoot_global_error_handler;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setError("SDK do AI Agent não disponível");
        setIsLoading(false);
      }
    };

    script.onerror = () => {
      if (window._chatwoot_global_error_handler) {
        window.removeEventListener(
          "error",
          window._chatwoot_global_error_handler
        );
        delete window._chatwoot_global_error_handler;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setError(
        `Erro ao carregar o script do AI Agent. Verifique se a URL base está correta: ${BASE_URL}`
      );
      setIsLoading(false);
    };

    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  };

  // Sobrescrever estilos do Chatwoot para widget maior nesta página
  const overrideChatwootStyles = () => {
    const checkAndOverride = () => {
      const chatwootStyles = document.getElementById("cw-widget-styles");
      if (chatwootStyles) {
        const customStyles = `
          @media only screen and (min-width: 667px) {
            .woot-widget-holder {
              width: 35vw !important;
              height: 85vh !important;
              max-height: 1000px !important;
            }
          }
          .woot-widget-bubble {
            animation: none !important;
          }
        `;
        chatwootStyles.textContent += customStyles;
        return true;
      }
      return false;
    };

    if (!checkAndOverride()) {
      setTimeout(() => {
        if (!checkAndOverride()) {
          setTimeout(checkAndOverride, 1000);
        }
      }, 500);
    }
  };

  // Limpar quando o componente desmontar
  useEffect(() => {
    return () => {
      cleanupChatwoot();
    };
  }, []);

  // Apenas preencher o campo com o token da URL, sem carregar automaticamente
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");

    if (tokenFromUrl && !websiteToken) {
      // Apenas atualiza o estado se vier da URL e o campo estiver vazio
      setWebsiteToken(tokenFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    loadChatwootWidget();
    // Atualizar URL com os parâmetros
    const newParams = new URLSearchParams();
    if (websiteToken) newParams.set("token", websiteToken);
    setSearchParams(newParams);
  };

  return (
    <div className="chats-page">
      <div className="chats-container">
        <p className="chats-description">
          Insira o WEBSITE_TOKEN para carregar o widget do AI Agent
          dinamicamente.
        </p>

        <form onSubmit={handleSubmit} className="chats-form">
          <div className="form-group">
            <label htmlFor="websiteToken">WEBSITE_TOKEN:</label>
            <input
              type="text"
              id="websiteToken"
              value={websiteToken}
              onChange={(e) => setWebsiteToken(e.target.value)}
              placeholder="Insira o WEBSITE_TOKEN"
              className="form-input"
              required
            />
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isLoading || !websiteToken}
          >
            {isLoading ? "A carregar..." : "Carregar Widget"}
          </button>
        </form>

        {error && (
          <div className="error-message">
            <strong>Erro:</strong> {error}
          </div>
        )}

        {isWidgetLoaded && (
          <div className="success-message">
            <strong>✅ Sucesso:</strong> Widget carregado com sucesso!
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatsPage;
